import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import {
    ListboxButton,
    ListboxPopover,
    ListboxList,
    ListboxOption,
    ListboxArrow,
    ListboxInput,
} from '@reach/listbox'
import type {PropsWithChildren} from 'react'
import React from 'react'
import styled, {css} from 'styled-components'

import chevronUp from '@helios/shared/images/icons/chevron-up.svg'
import {Text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'

type Option<TValue extends string> = {
    value: TValue
    label: string | JSX.Element
}

interface Props<TValue extends string> {
    onChange: (newValue: TValue) => void
    options: Option<TValue>[]
    triggerText?: string
    value: string
}

const Dropdown = <TValue extends string>({
    onChange,
    options,
    triggerText,
    value,
}: PropsWithChildren<Props<TValue>>) => (
    <ListboxInput value={value} onChange={onChange}>
        <StyledListboxButton>
            {(props) => (
                <>
                    <Text variant="heading-base" as="span">
                        {triggerText}{' '}
                        <Text variant="heading-base" as="span" color="brand">
                            {props.label}
                        </Text>
                    </Text>

                    <ListboxArrow>
                        <Arrow $isExpanded={props.isExpanded} />
                    </ListboxArrow>
                </>
            )}
        </StyledListboxButton>

        <StyledListboxPopover>
            <StyledListboxList>
                {options.map((option, i) => (
                    <StyledListboxOption key={`${option}-${i}`} value={option.value}>
                        <Text>{option.label}</Text>
                    </StyledListboxOption>
                ))}
            </StyledListboxList>
        </StyledListboxPopover>
    </ListboxInput>
)

export default Dropdown

export const StyledListboxButton = styled(ListboxButton)`
    display: inline-flex;
    text-align: left;
    cursor: pointer;

    user-select: none;
    background: none;
    border: none;
    align-items: center;
    justify-content: space-between;

    [data-reach-listbox-arrow] {
        display: block;
        margin-left: ${tokens.spacing20};
        font-size: 0.5em;
    }

    [data-reach-listbox-popover] {
        display: block;
        min-width: min-content;
        padding: 0.25rem 0;
        background: hsl(0, 0%, 100%);
        border: solid 1px hsla(0, 0%, 0%, 0.25);
        outline: none;
    }

    :focus-within {
        ${focusStyles()};
        border-radius: ${tokens.arc4};
    }
`

interface ArrowProps {
    $isExpanded: boolean
}

export const Arrow = styled.span<ArrowProps>(({$isExpanded}) => {
    const isExpandedStyles = css`
        transform: rotate(0);
    `

    return css`
        display: block;
        transform: rotate(-180deg);

        ${$isExpanded && isExpandedStyles}

        :after {
            content: url(${chevronUp});
        }
    `
})

const StyledListboxPopover = styled(ListboxPopover)`
    display: block;
    min-width: 320px;
    padding: ${tokens.spacing20};
    margin-top: 20px;
    background: ${colors.background.default};
    border: none;
    border-radius: ${radii.arc8};
    outline: none;

    &,
    :focus-within {
        outline: -webkit-focus-ring-color auto 4px;
        box-shadow: ${tokens.shadowElevateSubtle};
    }

    &[hidden] {
        display: none;
    }
`

const StyledListboxList = styled(ListboxList)`
    display: grid;
    padding: 0;
    margin: 0;
    list-style: none;
    gap: ${tokens.spacing20};

    :focus {
        outline: none;
        box-shadow: none;
    }
`

const StyledListboxOption = styled(ListboxOption)`
    display: block;
    padding: 0;
    margin: 0;

    white-space: nowrap;
    cursor: pointer;
    user-select: none;

    &[aria-selected='true'] {
        color: ${colors.text.brand};
        background: none;
    }
`
